import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { isTokenExpired } from '../libs/tokenUtils'
import { ArrowRightOnRectangleIcon, KeyIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import UpdatePasswordModal from './auth/UpdatePasswordModal'
import GlobalSearchBar from './GlobalSearchBar'

interface GlobalNavbarProps {
  transparent?: boolean
}

export default function GlobalNavbar({ transparent = false }: GlobalNavbarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  
  const token = localStorage.getItem('token')
  const roleSlug = localStorage.getItem('user_role_slug')
  const fullName = localStorage.getItem('user') || 'Pengguna'
  const email = localStorage.getItem('user_email') || ''
  
  const userId = localStorage.getItem('user_id')
  const isLoggedIn = !!(token && !isTokenExpired())
  const isAdmin = isLoggedIn && roleSlug !== 'user'

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = () => {
    localStorage.clear()
    navigate('/')
    window.location.reload()
  }

  const isDashboard = location.pathname.startsWith('/dashboard') || 
                      location.pathname.startsWith('/data') || 
                      location.pathname.startsWith('/user-management')

  return (
    <header className={`${transparent ? 'absolute w-full top-0 z-50 bg-transparent' : 'bg-white border-b border-neutral-200 sticky top-0 z-50'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <Link className="flex items-center gap-3" to="/">
            <img src="/logo.png" alt="logo" className="size-9" />
            <span className={`text-xl font-bold tracking-tight ${transparent ? 'text-white' : 'text-neutral-900'}`}>
              Profil Daerah
            </span>
          </Link>

          {isLoggedIn && !transparent && (
            <GlobalSearchBar />
          )}

          <div className="flex items-center gap-4">
            {!isAdmin && !isDashboard && (
              <button 
                className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
                  transparent 
                    ? 'border border-white/80 bg-white/10 text-white hover:bg-white hover:text-neutral-900' 
                    : 'text-neutral-600 hover:text-primary-600 hover:bg-primary-50'
                }`}
                onClick={() => navigate('/dataset')}
              >
                Jelajahi Dataset
              </button>
            )}

            {isAdmin && !isDashboard && (
              <button 
                className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
                  transparent 
                    ? 'border border-white/80 bg-white/10 text-white hover:bg-white hover:text-neutral-900' 
                    : 'text-neutral-600 hover:text-primary-600 hover:bg-primary-50'
                }`}
                onClick={() => navigate('/dashboard')}
              >
                Dashboard
              </button>
            )}

            {isLoggedIn ? (
              <div className="relative" ref={dropdownRef}>
                <button 
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className={`flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border transition-all ${
                    transparent
                      ? 'border-white/30 bg-white/10 text-white hover:bg-white/20'
                      : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 shadow-sm'
                  }`}
                >
                  <div className={`size-7 rounded-full flex items-center justify-center font-bold text-xs ${
                    transparent ? 'bg-white text-primary-600' : 'bg-primary-100 text-primary-700'
                  }`}>
                    {fullName.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm font-medium hidden sm:block max-w-[120px] truncate">{fullName}</span>
                  <ChevronDownIcon className="size-4" />
                </button>

                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-neutral-100 py-1 overflow-hidden origin-top-right animate-in fade-in zoom-in duration-200">
                    <div className="px-4 py-3 border-b border-neutral-100">
                      <p className="text-sm font-bold text-neutral-900 truncate">{fullName}</p>
                      <p className="text-xs text-neutral-500 truncate">{email}</p>
                      <span className="inline-block mt-1 px-2 py-0.5 bg-primary-50 text-primary-700 text-[10px] font-bold uppercase rounded-full tracking-wider">
                        {roleSlug?.replace('_', ' ')}
                      </span>
                    </div>
                    
                    <button 
                      onClick={() => {
                        setIsDropdownOpen(false)
                        setIsPasswordModalOpen(true)
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 flex items-center gap-2 transition-colors"
                    >
                      <KeyIcon className="size-4 text-neutral-400" />
                      Ganti Password
                    </button>
                    
                    <button 
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors border-t border-neutral-100"
                    >
                      <ArrowRightOnRectangleIcon className="size-4 text-red-500" />
                      Keluar
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button 
                className={`text-sm font-medium px-6 py-2 rounded-lg transition-colors shadow-sm ${
                  transparent
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                }`}
                onClick={() => navigate('/login')}
              >
                Masuk
              </button>
            )}
          </div>
        </div>
      </div>
      {isLoggedIn && (
        <UpdatePasswordModal
          isOpen={isPasswordModalOpen}
          setIsOpen={setIsPasswordModalOpen}
          request={{id: userId ?? ''}}
        />
      )}
    </header>
  )
}
